import React from 'react'

import Store from 'store'

import { CallbackWrap } from '@/types/common'
import { SkillsEmbed, SkillsEmbedSkill } from '@/types/embed'
import { Gem, Item } from '@/types/item'
import { faChevronDown, faChevronUp, faEdit, faTimes } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import classNames from 'classnames'
import { SmallIcon } from 'components/Icon'
import ItemLibrary, { LibraryClickHandler } from 'components/ItemLibrary'
import TextEdit from 'components/TextEdit'
import { CopyEmbedBox } from 'components/copy'
import Data from 'data'
import { itemCategoryEquipment } from 'library'
import { gemCreate, gemItemIcon, gemMaxLevel } from 'logic/gem'
import { itemBaseIcon, itemGetBaseColor, itemGetName } from 'logic/item'
import { getGemColor } from 'logic/mods'
import { WithItemTemplateTooltip, WithItemTooltip } from 'tooltip'
import { GemTooltip } from 'tooltip/ItemTooltip'
import Dialog from 'ui/dialog'
import NumberInput from 'ui/number'
import Select, { OptionOrGroup, SelectOption, underlinerFunc } from 'ui/select'
import { RawTooltip } from 'ui/tooltip'
import SkillGems, { skillItemTypes } from './SkillGems'
import SkillGemsVideoDisplay from './SkillGemsVideoDisplay'

function SkillStep({
  index,
  active,
  setActive
}: {
  index: number
  active: number
  setActive: (active: number) => void
}) {
  const [editing, setEditing] = React.useState(false)
  const embed = Store.useEmbed() as SkillsEmbed
  const dispatch = Store.useDispatch()

  const onClick = React.useCallback(
    (e: React.MouseEvent) => {
      if ((e.target as Element).tagName === 'INPUT') return
      ;(document.activeElement as HTMLElement | null)?.blur()
      setActive(index)
    },
    [index, setActive]
  )

  const onEdit = React.useCallback((e: React.UIEvent) => {
    e.stopPropagation()
    setEditing((flag) => !flag)
  }, [])
  const onEditChange = React.useCallback(
    (name: string | undefined) => {
      setEditing(false)
      if (name) {
        dispatch.setEmbed((embed) => {
          if (embed.type !== 'skills' && embed.type !== 'skills-video-display') return embed
          embed = { ...embed }
          embed.steps = embed.steps.slice()
          embed.steps[index] = { ...embed.steps[index], name }
          return embed
        })
      }
    },
    [dispatch, index]
  )
  const onDelete = React.useCallback(
    (e: React.UIEvent) => {
      e.stopPropagation()
      if (embed.steps.length <= 1) return
      if (index >= embed.steps.length - 1) {
        setActive(index - 1)
      }
      dispatch.setEmbed((embed) => {
        if (embed.type !== 'skills' && embed.type !== 'skills-video-display') return embed
        if (embed.steps.length <= 1) return embed
        embed = { ...embed }
        embed.steps = embed.steps.slice()
        embed.steps.splice(index, 1)
        return embed
      })
    },
    [dispatch, embed, index, setActive]
  )

  const onMoveUp = React.useCallback(
    (e: React.UIEvent) => {
      e.stopPropagation()
      if (index > 0) {
        setActive(index - 1)
      }
      dispatch.setEmbed((embed) => {
        if (embed.type !== 'skills' && embed.type !== 'skills-video-display') return embed
        if (index <= 0) return embed
        embed = { ...embed }
        embed.steps = embed.steps.slice()
        const value = embed.steps.splice(index, 1)
        embed.steps.splice(index - 1, 0, ...value)
        return embed
      })
    },
    [dispatch, index, setActive]
  )
  const onMoveDown = React.useCallback(
    (e: React.UIEvent) => {
      e.stopPropagation()
      if (index < embed.steps.length - 1) {
        setActive(index + 1)
      }
      dispatch.setEmbed((embed) => {
        if (embed.type !== 'skills' && embed.type !== 'skills-video-display') return embed
        if (index >= embed.steps.length - 1) return embed
        embed = { ...embed }
        embed.steps = embed.steps.slice()
        const value = embed.steps.splice(index, 1)
        embed.steps.splice(index + 1, 0, ...value)
        return embed
      })
    },
    [dispatch, embed, index, setActive]
  )

  const data = embed.steps[index]
  if (!data) return null

  return (
    <li
      className={classNames({
        'poe-active': active === index,
        'poe-editing': editing
      })}
      onClick={onClick}>
      {editing ? <TextEdit value={data.name} onChange={onEditChange} /> : data.name}
      <FontAwesomeIcon
        className={classNames('poe-delete', {
          'poe-disabled': embed.steps.length <= 1
        })}
        icon={faTimes}
        onClick={onDelete}
      />
      <FontAwesomeIcon className="poe-rename" icon={faEdit} onClick={onEdit} />
      <FontAwesomeIcon
        className={classNames('poe-move', {
          'poe-disabled': index >= embed.steps.length - 1
        })}
        icon={faChevronDown}
        onClick={onMoveDown}
      />
      <FontAwesomeIcon
        className={classNames('poe-move', { 'poe-disabled': index <= 0 })}
        icon={faChevronUp}
        onClick={onMoveUp}
      />
    </li>
  )
}

function gemOption(gem: Gem | undefined) {
  const data = gem && Data.gems[gem.id]
  if (!data) return undefined
  return {
    id: gem.id,
    name: data.active_skill?.display_name ?? data.base_item?.display_name ?? 'Unknown'
  }
}

const GemTemplateTooltip = ({ id, base }: { id: string; base?: Gem }) => <GemTooltip gem={gemCreate(id, base)} />

export function GemEditor({
  gem,
  onChange
}: {
  gem: Gem | undefined
  onChange: (value: CallbackWrap<Gem | undefined>) => void
}) {
  const getOptions = React.useCallback((): OptionOrGroup<SelectOption<string>>[] => {
    const active = [],
      support = []
    for (const [id, gem] of Object.entries(Data.gems)) {
      if (!gem.base_item) continue
      const name = gem.active_skill?.display_name ?? gem.base_item?.display_name ?? 'Unknown'
      if (gem.is_support) {
        support.push({ id, name })
      } else {
        active.push({ id, name })
      }
    }
    return [
      {
        title: 'Active Skill Gems',
        options: active.sort((a, b) => a.name.localeCompare(b.name))
      },
      {
        title: 'Support Skill Gems',
        options: support.sort((a, b) => a.name.localeCompare(b.name))
      }
    ]
  }, [])

  const onSetGem = React.useCallback(
    (opt?: SelectOption<string>) => onChange((gem) => (opt ? gemCreate(opt.id, gem) : undefined)),
    [onChange]
  )
  const onSetLevel = React.useCallback(
    (value: number) =>
      onChange((gem) => {
        if (!gem) return gem
        gem = { ...gem, level: value }
        if (value > gemMaxLevel(gem.id)) {
          gem.corrupted = true
        }
        return gem
      }),
    [onChange]
  )
  const onSetQuality = React.useCallback(
    (value: number) =>
      onChange((gem) => {
        if (!gem) return gem
        gem = { ...gem, quality: value }
        if (value > 20) {
          gem.corrupted = true
        }
        return gem
      }),
    [onChange]
  )
  const onSetCorrupted = React.useCallback(
    () =>
      onChange((gem) => {
        if (!gem || Data.gems[gem.id].tags?.includes('vaal')) return gem
        gem = { ...gem, corrupted: !gem.corrupted }
        if (!gem.corrupted) {
          gem.level = Math.min(gemMaxLevel(gem.id), gem.level)
          gem.quality = Math.min(20, gem.quality)
        }
        return gem
      }),
    [onChange]
  )

  const renderGem = React.useCallback(
    ({ id, name }: SelectOption<string>, search?: RegExp) => {
      const icon = gemItemIcon(Data.gems[id])
      return (
        <RawTooltip
          tooltip={gem && id === gem?.id ? <GemTooltip gem={gem} /> : <GemTemplateTooltip id={id} base={gem} />}>
          <div className="poe-item-gem">
            {icon != null && <SmallIcon icon={icon} />}
            {underlinerFunc(name, search)}
          </div>
        </RawTooltip>
      )
    },
    [gem]
  )

  return (
    <div className="poe-GemEditor">
      <Select
        options={getOptions}
        value={gemOption(gem)}
        onChange={onSetGem}
        renderOption={renderGem}
        renderValue={renderGem}
        searchLimit={10}
        searchMiddle
        allowEmpty
        virtualScroll
        placeholder="Select gem..."
        className="poe-gem"
      />
      {!!gem && (
        <NumberInput
          className="poe-level"
          min={1}
          max={gemMaxLevel(gem.id) + 1}
          value={gem.level}
          onChange={onSetLevel}
        />
      )}
      {!!gem && (
        <>
          <span>+</span>
          <NumberInput className="poe-quality" min={0} max={23} value={gem.quality} onChange={onSetQuality} />
          <span>%</span>
        </>
      )}
      {!!(gem && !Data.gems[gem.id].tags?.includes('vaal')) && (
        <div
          className={classNames('poe-corrupted', {
            'poe-active': gem.corrupted
          })}
          onClick={onSetCorrupted}>
          <SmallIcon icon={Data.items['Metadata/Items/Currency/CurrencyCorrupt'].visual_identity.dds_file} />
        </div>
      )}
    </div>
  )
}

function SkillStepGem({ step, skill, index }: { step: number; skill: number; index: number }) {
  const skillData = Store.useEmbed((embed) => (embed as SkillsEmbed).steps[step]?.skills[skill])
  const length = skillData?.gems.length
  const link = skillData?.unlink?.[index]
  const data = skillData?.gems[index]
  const dispatch = Store.useDispatch()

  const onChange = React.useCallback(
    (value: CallbackWrap<Gem | undefined>) =>
      dispatch.setEmbed((embed) => {
        if (embed.type !== 'skills' && embed.type !== 'skills-video-display') return embed
        const prev = embed.steps[step].skills[skill].gems[index]
        if (typeof value === 'function') value = value(prev)
        if (value === prev) return embed
        embed = { ...embed }
        embed.steps = embed.steps.slice()
        const st = (embed.steps[step] = { ...embed.steps[step] })
        st.skills = st.skills.slice()
        const sk = (st.skills[skill] = { ...st.skills[skill] })
        sk.gems = sk.gems.slice()
        if (value) {
          sk.gems[index] = value
        } else {
          sk.gems.splice(index, 1)
          if (sk.unlink) {
            sk.unlink = sk.unlink.slice()
            sk.unlink.splice(index, 1)
          }
        }
        return embed
      }),
    [dispatch, step, skill, index]
  )

  const onToggleLink = React.useCallback(
    () =>
      dispatch.setEmbed((embed) => {
        if (embed.type !== 'skills' && embed.type !== 'skills-video-display') return embed
        embed = { ...embed }
        embed.steps = embed.steps.slice()
        const st = (embed.steps[step] = { ...embed.steps[step] })
        st.skills = st.skills.slice()
        const sk = (st.skills[skill] = { ...st.skills[skill] })
        sk.unlink = sk.unlink ? sk.unlink.slice() : sk.gems.map(() => false)
        sk.unlink[index] = !sk.unlink[index]
        return embed
      }),
    [dispatch, step, skill, index]
  )

  const gemData = data && Data.gems[data.id]
  const color = gemData ? getGemColor(gemData) : 'white'

  return (
    <li className="poe-SkillStepGem">
      <div
        className={classNames(`poe-SocketImage poe-socket-${color}`, gemData && `poe-gem-${color}`, {
          'poe-socket-support': gemData && gemData.is_support
        })}>
        <div className="poe-image" />
        {index < length - 1 && (
          <div className={classNames('poe-socket-link', { 'poe-active': link })} onClick={onToggleLink} />
        )}
      </div>
      <GemEditor gem={data} onChange={onChange} />
    </li>
  )
}

const renderType = ({ id, name }: SelectOption<number>) => (
  <span>
    <span className="poe-item-type" style={{ backgroundPositionX: `-${id}em` }} /> {name}
  </span>
)

function renderLibraryItem(item: Item, searchRe?: RegExp) {
  const icon = itemBaseIcon((item.unique && Data.uniques[item.unique]) || Data.items[item.base], item.map_tier)
  const color = itemGetBaseColor(Data.items[item.base]) || item.rarity || 'normal'
  return (
    <WithItemTooltip item={item}>
      <span className={`poe-item-${color}`}>
        <SmallIcon icon={icon} /> {underlinerFunc(itemGetName(item), searchRe)}
      </span>
    </WithItemTooltip>
  )
}

function BaseSelector({ onSelect }: { onSelect: (value: string | undefined) => void }) {
  const onClick = React.useCallback<LibraryClickHandler>((e, id) => onSelect(id), [onSelect])

  return (
    <Dialog className="poe-ItemSelector" modal title="Select Item" onClose={() => onSelect(undefined)}>
      <div className="poe-ItemSelector-content">
        <ItemLibrary root={itemCategoryEquipment} onClick={onClick} uniques />
      </div>
    </Dialog>
  )
}

type ItemOption = {
  id: string
  name: string
  item?: Item
  type?: number
}

function SkillStepItem({
  data,
  onChange
}: {
  data: SkillsEmbedSkill
  onChange: (value: (prev: SkillsEmbedSkill) => SkillsEmbedSkill) => void
}) {
  const [selecting, setSelecting] = React.useState(false)
  const store = Store.useStore()
  const getOptions = React.useCallback(() => {
    const options: OptionOrGroup<ItemOption>[] = []
    options.push({ id: 'select', name: 'Select base...' })
    const custom: ItemOption[] = []
    const state = store.getState()
    if (state.items) {
      for (const [id, item] of Object.entries(state.items)) {
        if (!item) continue
        const base = Data.items[item.base]
        if (!base || !itemCategoryEquipment.filter(base)) continue
        custom.push({ id, name: itemGetName(item), item })
      }
    }
    if (custom.length) options.push({ title: 'Custom Items', options: custom })
    options.push({
      title: 'Types',
      options: skillItemTypes.map((t, i) => ({
        id: `type${i}`,
        name: t,
        type: i
      }))
    })
    return options
  }, [store])
  const renderOption = React.useCallback((opt: ItemOption, searchRe?: RegExp) => {
    if (opt.item) {
      return renderLibraryItem(opt.item, searchRe)
    } else if (opt.type != null) {
      return renderType({ id: opt.type, name: opt.name })
    } else {
      return <span>{opt.name}</span>
    }
  }, [])
  const renderValue = React.useCallback(() => {
    if (typeof data.item === 'number') {
      const state = store.getState().items
      if (state && state[data.item]) {
        return renderLibraryItem(state[data.item]!)
      }
    } else if (typeof data.item === 'string') {
      const item = Data.uniques[data.item] ? Data.uniques[data.item] : Data.items[data.item]
      if (item) {
        const color = Data.uniques[data.item] ? 'unique' : itemGetBaseColor(Data.items[data.item]) || 'normal'
        const icon = itemBaseIcon(item)
        return (
          <WithItemTemplateTooltip name={data.item}>
            <span className={`poe-item-${color}`}>
              {!!icon && <SmallIcon icon={icon} />} {item.name}
            </span>
          </WithItemTemplateTooltip>
        )
      }
    }
    const type = data.type ?? 0
    return renderType({ id: type, name: skillItemTypes[type] })
  }, [data, store])
  const onSelectBase = React.useCallback(
    (id: string | undefined) => {
      setSelecting(false)
      if (id) {
        onChange((skill) => {
          skill = { ...skill }
          delete skill.type
          skill.item = id
          return skill
        })
      }
    },
    [onChange]
  )
  const onSelect = React.useCallback(
    (opt?: ItemOption) => {
      if (opt) {
        if (opt.id === 'select') {
          setSelecting(true)
        } else if (opt.item) {
          onChange((skill) => {
            skill = { ...skill }
            delete skill.type
            skill.item = Number(opt.id)
            return skill
          })
        } else if (opt.type != null) {
          onChange((skill) => {
            skill = { ...skill }
            delete skill.item
            skill.type = opt.type
            return skill
          })
        }
      } else {
        onChange((skill) => {
          skill = { ...skill }
          delete skill.item
          skill.type = 0
          return skill
        })
      }
    },
    [onChange]
  )
  return (
    <>
      <Select
        options={getOptions}
        value={data.item ? { id: String(data.item), name: '' } : { id: `type${data.type ?? 0}`, name: '' }}
        renderOption={renderOption}
        renderValue={renderValue}
        searchLimit={10}
        searchMiddle
        allowEmpty
        onChange={onSelect}
        className="ui-Select"
      />
      {!!selecting && <BaseSelector onSelect={onSelectBase} />}
    </>
  )
}

function SkillStepSkill({ step, index }: { step: number; index: number }) {
  const stepData = Store.useEmbed((embed) => (embed as SkillsEmbed).steps[step])
  const skillData = stepData?.skills[index]
  const dispatch = Store.useDispatch()
  const onChangeItem = React.useCallback(
    (value: SkillsEmbedSkill | ((prev: SkillsEmbedSkill) => SkillsEmbedSkill)) =>
      dispatch.setEmbed((embed) => {
        if (embed.type !== 'skills' && embed.type !== 'skills-video-display') return embed
        const prev = embed.steps[step]?.skills[index]
        if (typeof value === 'function') value = value(prev)
        if (value === prev) return embed
        embed = { ...embed }
        embed.steps = embed.steps.slice()
        const st = (embed.steps[step] = { ...embed.steps[step] })
        st.skills = st.skills.slice()
        st.skills[index] = value
        return embed
      }),
    [step, index, dispatch]
  )
  const onMoveUp = React.useCallback(
    () =>
      dispatch.setEmbed((embed) => {
        if (embed.type !== 'skills' && embed.type !== 'skills-video-display') return embed
        if (index <= 0) return embed
        embed = { ...embed }
        embed.steps = embed.steps.slice()
        const st = (embed.steps[step] = { ...embed.steps[step] })
        st.skills = st.skills.slice()
        const value = st.skills.splice(index, 1)
        st.skills.splice(index - 1, 0, ...value)
        return embed
      }),
    [dispatch, step, index]
  )
  const onMoveDown = React.useCallback(
    () =>
      dispatch.setEmbed((embed) => {
        if (embed.type !== 'skills' && embed.type !== 'skills-video-display') return embed
        if (index >= embed.steps[step].skills.length - 1) return embed
        embed = { ...embed }
        embed.steps = embed.steps.slice()
        const st = (embed.steps[step] = { ...embed.steps[step] })
        st.skills = st.skills.slice()
        const value = st.skills.splice(index, 1)
        st.skills.splice(index + 1, 0, ...value)
        return embed
      }),
    [dispatch, step, index]
  )
  const onDelete = React.useCallback(
    () =>
      dispatch.setEmbed((embed) => {
        if (embed.type !== 'skills' && embed.type !== 'skills-video-display') return embed
        embed = { ...embed }
        embed.steps = embed.steps.slice()
        const st = (embed.steps[step] = { ...embed.steps[step] })
        st.skills = st.skills.slice()
        st.skills.splice(index, 1)
        return embed
      }),
    [dispatch, step, index]
  )
  if (!skillData) return null
  return (
    <li className="poe-skill-group">
      <div className="poe-header">
        <SkillStepItem data={skillData} onChange={onChangeItem} />
        <FontAwesomeIcon
          className={classNames('poe-move', { 'poe-disabled': index <= 0 })}
          icon={faChevronUp}
          onClick={onMoveUp}
        />
        <FontAwesomeIcon
          className={classNames('poe-move', {
            'poe-disabled': index >= stepData.skills.length - 1
          })}
          icon={faChevronDown}
          onClick={onMoveDown}
        />
        <FontAwesomeIcon className="poe-delete" icon={faTimes} onClick={onDelete} />
      </div>
      <ul>
        {skillData.gems.map((_, gid) => (
          <SkillStepGem key={gid} step={step} skill={index} index={gid} />
        ))}
        <SkillStepGem step={step} skill={index} index={skillData.gems.length} />
      </ul>
    </li>
  )
}

export default function SkillEditor() {
  const embed = Store.useEmbed() as SkillsEmbed
  const step = embed.steps[embed.step] ? embed.step : 0
  const dispatch = Store.useDispatch()
  const setStep = React.useCallback((step: number) => dispatch.setEmbed((embed) => ({ ...embed, step })), [dispatch])
  const onAddStep = React.useCallback(
    () =>
      dispatch.setEmbed((embed) => {
        if (embed.type !== 'skills' && embed.type !== 'skills-video-display') return embed
        embed = { ...embed }
        embed.steps = embed.steps.slice()
        embed.steps.push({
          ...embed.steps[embed.steps.length - 1],
          name: `Step ${embed.steps.length + 1}`
        })
        embed.step = embed.steps.length - 1
        return embed
      }),
    [dispatch]
  )
  const onAddSkill = React.useCallback(
    () =>
      dispatch.setEmbed((embed) => {
        if (embed.type !== 'skills' && embed.type !== 'skills-video-display') return embed
        embed = { ...embed }
        embed.steps = embed.steps.slice()
        const st = (embed.steps[step] = { ...embed.steps[step] })
        st.skills = [...st.skills, { type: 0, gems: [] }]
        return embed
      }),
    [dispatch, step]
  )
  const maxSkills = Math.max(...embed.steps.map((step) => step?.skills.length || 0))
  const [isVideoDisplay, setIsVideoDisplay] = React.useState(false)
  const [isSeparateItems, setIsSeparateItems] = React.useState(false)

  return (
    <div className="poe-SkillEditor">
      <div className="poe-SkillEditor-setup">
        <div className="poe-left">
          <ul className="poe-steps">
            {embed.steps.map((_, index) => (
              <SkillStep key={index} index={index} active={step} setActive={setStep} />
            ))}
            <li onClick={onAddStep}>Add Step</li>
          </ul>
          <label className="poe-is-video-display">
            <input type="checkbox" checked={isVideoDisplay} onChange={(e) => setIsVideoDisplay(!isVideoDisplay)} />
            Display for Video Embedding
          </label>
          <br />
          {isVideoDisplay && (
            <>
              <label>
                <input
                  type="checkbox"
                  checked={isSeparateItems}
                  onChange={(e) => setIsSeparateItems(!isSeparateItems)}
                />
                Separate Items
              </label>
            </>
          )}
          {!isVideoDisplay && (
            <>
              <SkillGems steps={embed.steps} active={step} setActive={setStep} />
              <CopyEmbedBox type="skills" height={32 + 47 * maxSkills + (embed.steps.length > 1 ? 47 : 0)} />
            </>
          )}
        </div>
        <ul className="poe-content">
          {!!embed.steps[step] &&
            embed.steps[step].skills.map((_, index) => <SkillStepSkill key={index} step={step} index={index} />)}
          <li className="poe-add-skill">
            <span onClick={onAddSkill}>Add Skill Group</span>
          </li>
        </ul>
      </div>

      {isVideoDisplay && (
        <SkillGemsVideoDisplay
          showSettings={true}
          separateItems={isSeparateItems}
          steps={embed.steps}
          active={step}
          setActive={setStep}
        />
      )}
    </div>
  )
}
